import { ChevronDown } from 'lucide-react';
import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from 'react';

import styles from './Field.module.css';
import { Icon } from './Icon';

type Option = string | { value: string; label: string };

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement> & {
    label: ReactNode;
    hint?: ReactNode;
    error?: string;
    options: Option[];
    placeholder?: string;
  }
>(function Select({ label, hint, error, options, placeholder, id, className, ...rest }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const messageId = `${fieldId}-message`;
  const message = error || hint;
  return (
    <div className={`${styles.field} ${className ?? ''}`}>
      <label htmlFor={fieldId} className={styles.label}>
        {label}
      </label>
      <span className={`${styles.control} ${error ? styles.invalid : ''}`}>
        <span className={styles.selectWrap}>
          <select
            {...rest}
            ref={ref}
            id={fieldId}
            aria-invalid={error ? true : undefined}
            aria-describedby={message ? messageId : undefined}
            className={styles.select}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => {
              const value = typeof option === 'string' ? option : option.value;
              const text = typeof option === 'string' ? option : option.label;
              return (
                <option key={value} value={value}>
                  {text}
                </option>
              );
            })}
          </select>
          <span className={styles.chevron}>
            <Icon icon={ChevronDown} size={18} />
          </span>
        </span>
      </span>
      {message && (
        <span id={messageId} className={error ? styles.error : styles.hint}>
          {message}
        </span>
      )}
    </div>
  );
});
